import { mapGetters, mapActions, mapMutations } from 'vuex'

import Sidebar from '@/components/Sidebar/Sidebar.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'
import { pageTypes } from '@/utils/pageVariables'

export default {
  name: 'PagesEcom',
  components: {
    Sidebar,
    ChatGPT
  },
  data () {
    return {
      isLoading: {
        pages: false,
        exclude: false,
        reset: false,
        updatePageType: false,
        refresh: false
      },

      statuses: [
        { text: 'Category', value: 'category', count: 'categoryCount', pageTypes: ['category'] },
        { text: 'Products', value: 'products', count: 'productsCount', pageTypes: ['product'] },
        { text: 'Blog', value: 'blog', count: 'blogCount', pageTypes: ['article', 'asset', 'listicle', 'glossary'] },
        { text: 'Other', value: 'other', count: 'otherCount', pageTypes: ['home', 'other'] },
        { text: 'All Not Excluded', value: 'allNotExcluded', count: 'activeCount' },
        { text: 'Excluded', value: 'excluded', count: 'excludedCount' },
        { text: 'All', value: 'all', count: 'totalCount' }
      ],
      selectedStatus: 'category',
      excluded: '0',

      selectedRows: [],
      filterUrl: '',
      typeFilter: 'category',
      lastSelectedIndex: null,

      showPageTypeDialog: false,
      pageToUpdateType: null,

      tableFooter: {
        itemsPerPage: 100,
        page: 1,
        sortBy: ['google_impressions_last'],
        sortDesc: [true]
      },
      tableHeaders: {
        headers: [
          { text: 'URL', value: 'url' },
          { text: 'Cluster', value: 'title' },
          { text: 'Type', value: 'type' },
          { text: 'Status', value: 'status_code' },
          { text: 'Impressions', value: 'google_impressions_last' },
          { text: 'Clicks', value: 'google_clicks_last' },
          { text: 'Rank', value: 'google_position_last' },
          { text: 'Internal links', value: 'inLinksCount' },
          { text: 'Published at', value: 'published_at' },
          { value: 'actions', sortable: false, align: 'end' }
        ]
      },
      pageTypes,
      showCopilot: false
    }
  },
  async mounted () {
    await this.loadData()
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    tableFooter: {
      async handler () {
        if (!this.isLoading.page) {
          await this.loadData()
        }
      },
      deep: true
    },
    selectedRows (val) {
      if (!val || val.length === 0) {
        this.lastSelectedIndex = null
      }
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'pages',
      'activeContentplan',
      'activeWorkspace'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan?.name ?? ''
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    showResetAction () {
      if (this.selectedRows?.length < 1) return
      if (this.selectedRows.some(page => page.excluded)) return true
    },
    showExcludeAction () {
      if (this.selectedRows?.length < 1) return
      if (this.selectedRows.some(page => !page.excluded)) return true
    },
    showBulkTypeAction () {
      if (this.selectedRows?.length < 1) return false
      return this.selectedRows.some(page => !page.excluded)
    },
    isAnyRowSelected () {
      return this.selectedRows?.length > 0
    },
    pagesWithCounts () {
      if (!this.pages || !this.pages.items) return { pages: [], pageCounts: {} }

      // Use server-provided counts instead of calculating client-side
      return {
        pages: this.pages.items,
        pageCounts: this.pages.meta?.counts || {}
      }
    },
    allPageTypes () {
      return pageTypes
    },
    computedTableHeaders () {
      const base = this.tableHeaders.headers.slice()
      if (this.typeFilter === 'product') {
        // Insert after URL column (index 0)
        const insertAt = 1
        base.splice(insertAt, 0,
          { text: 'Parent Category', value: 'parent_category', sortable: false },
          { text: 'Stock', value: 'stock_status', sortable: false }
        )
      }
      return base
    },
    hierarchicalPages () {
      if (!this.pages || !this.pages.items) return []

      // Only apply hierarchy for categories; otherwise return server-ordered pages
      if (this.selectedStatus !== 'category') {
        return this.pages.items
      }

      const items = this.pages.items
      if (!items.length) return items

      // Build quick lookups for hierarchy by category key
      const keyFor = (p) => p.category_name || p.url
      const byKey = new Map()
      const childrenByParent = new Map()

      items.forEach(p => {
        byKey.set(keyFor(p), p)
        const parentKey = p.parent_category || null
        if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, [])
        childrenByParent.get(parentKey).push(p)
      })

      // Sorting comparator aligned with current table sort (default: impressions desc)
      const sortKey = (this.tableFooter && this.tableFooter.sortBy && this.tableFooter.sortBy[0]) || 'google_impressions_last'
      const desc = (this.tableFooter && this.tableFooter.sortDesc && this.tableFooter.sortDesc[0]) !== false // default true
      const compare = (a, b) => {
        const av = a?.[sortKey]
        const bv = b?.[sortKey]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (av < bv) return desc ? 1 : -1
        if (av > bv) return desc ? -1 : 1
        return 0
      }

      // Sort children lists based on the active comparator
      for (const [k, arr] of childrenByParent.entries()) {
        console.log('key', k)
        arr.sort(compare)
      }

      // Base order: all items sorted by current comparator (server already sorts, this is a safeguard)
      const baseOrdered = items.slice().sort(compare)
      const result = []
      const visited = new Set()

      const addSubtree = (parent, level) => {
        const key = keyFor(parent)
        const kids = childrenByParent.get(key) || []
        for (const child of kids) {
          if (visited.has(child.page_id)) continue
          child._hierarchyLevel = level
          result.push(child)
          visited.add(child.page_id)
          addSubtree(child, level + 1)
        }
      }

      for (const page of baseOrdered) {
        if (visited.has(page.page_id)) continue

        const parentKey = page.parent_category || null
        const parentExistsInSet = parentKey && byKey.has(parentKey)

        // If this is a child and its parent exists in the dataset but hasn't been rendered yet,
        // skip for now; it will appear under the parent when the parent is processed.
        if (parentExistsInSet) {
          const parent = byKey.get(parentKey)
          if (!visited.has(parent.page_id)) continue
        }

        // Render the page (top-level or orphan) and then its subtree
        page._hierarchyLevel = 0
        result.push(page)
        visited.add(page.page_id)
        addSubtree(page, 1)
      }

      // Add any remaining items not yet visited (safety for edge cases)
      if (visited.size !== items.length) {
        for (const page of baseOrdered) {
          if (!visited.has(page.page_id)) {
            page._hierarchyLevel = 0
            result.push(page)
            visited.add(page.page_id)
            addSubtree(page, 1)
          }
        }
      }

      return result
    }
  },
  methods: {
    ...mapActions([
      'pageGet',
      'pagePut'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    itemClassPrepend (item) {
      return item.excluded ? 'excluded' : ''
    },
    getArticleLink (articleId) {
      if (!articleId) return '#'
      return `/workspace/${this.workspaceId}/contentplan/${this.contentplanId}/article/${articleId}`
    },
    async loadData () {
      if (this.isLoading.pages) return
      this.isLoading.pages = true
      document.title = 'Pages | ContentGecko'
      console.log('Loading pages', this.tableFooter.sortBy[0], this.tableFooter.sortDesc[0])

      try {
        await this.pageGet({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          limit: this.tableFooter.itemsPerPage || 100,
          page: this.tableFooter.page || 1,
          search: this.filterUrl || '.*',
          excluded: this.excluded,
          typeFilter: this.typeFilter,
          sortColumn: this.tableFooter.sortBy[0] || 'google_impressions_last',
          sortDirection: this.tableFooter.sortDesc[0] ? 'desc' : 'asc'
        })
      } catch (e) {
        console.error('Error loading pages:', e)
      }

      this.isLoading.pages = false
    },
    isStatusSelected (status) {
      if (status === 'all' && this.excluded === null && this.typeFilter === null) return true
      if (status === 'allNotExcluded' && this.excluded === '0' && this.typeFilter === null) return true
      if (status === 'excluded' && this.excluded === '1') return true

      const statusObj = this.statuses.find(s => s.value === status)
      if (statusObj && statusObj.pageTypes) {
        return this.excluded === '0' && this.typeFilter && statusObj.pageTypes.includes(this.typeFilter.split(',')[0])
      }

      return false
    },
    async handleStatusClick (statusValue) {
      this.selectedStatus = statusValue

      if (statusValue === 'all') {
        this.excluded = null
        this.typeFilter = null
      } else if (statusValue === 'excluded') {
        this.excluded = '1'
        this.typeFilter = null
      } else {
        this.excluded = '0'
        const statusObj = this.statuses.find(s => s.value === statusValue)
        if (statusObj && statusObj.pageTypes) {
          // For single page type tabs, set specific type filter
          if (statusObj.pageTypes.length === 1) {
            this.typeFilter = statusObj.pageTypes[0]
          } else {
            // For multi-type tabs like Blog, we'll handle filtering differently in the API call
            this.typeFilter = statusObj.pageTypes.join(',')
          }
        } else {
          this.typeFilter = null
        }
      }

      this.tableFooter.page = 1
      await this.loadData()
    },
    async excludeRow (pageId) {
      if (!this.selectedRows?.length && !pageId) return

      this.isLoading.exclude = true
      const selectedPageIds = this.selectedRows.map(el => el.page_id)
      const pageIds = pageId ? [pageId] : selectedPageIds

      try {
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          pageIds,
          action: 'exclude'
        })
        this.selectedRows = []
      } catch (err) {
        console.error('Error excluding pages:', err)
      } finally {
        await this.loadData()
        this.isLoading.exclude = false
      }
    },
    async resetRow (pageId) {
      if (!this.selectedRows?.length && !pageId) return

      this.isLoading.reset = true
      const selectedPageIds = this.selectedRows.map(el => el.page_id)
      const pageIds = pageId ? [pageId] : selectedPageIds

      try {
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          pageIds,
          action: 'removeExclusion'
        })
        this.selectedRows = []
      } catch (err) {
        console.error('Error resetting pages:', err)
      } finally {
        await this.loadData()
        this.isLoading.reset = false
      }
    },
    formatDate (date) {
      if (!date) return
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    async updatePageType (pageId, newType) {
      if (!pageId || !newType) return

      this.isLoading.updatePageType = true
      try {
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          pageIds: [pageId],
          action: 'setPageType',
          pageType: newType
        })
        await this.loadData()
        this.setSnackbar({ open: true, text: 'Page type updated successfully', type: 'success' })
      } catch (error) {
        console.error('Error updating page type:', error)
        this.setSnackbar({ open: true, text: 'Error updating page type', type: 'error' })
      } finally {
        this.isLoading.updatePageType = false
      }
    },
    async updatePageTypeBulk (newType) {
      if (!newType) return
      const applicablePages = (this.selectedRows || []).filter(page => !page.excluded)
      if (!applicablePages.length) {
        this.setSnackbar({ open: true, text: 'Select at least one non-excluded page to update the type.', type: 'info' })
        return
      }

      this.isLoading.updatePageType = true
      try {
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          pageIds: applicablePages.map(page => page.page_id),
          action: 'setPageType',
          pageType: newType
        })
        this.selectedRows = []
        await this.loadData()
        this.setSnackbar({ open: true, text: 'Page types updated successfully', type: 'success' })
      } catch (error) {
        console.error('Error updating page types:', error)
        this.setSnackbar({ open: true, text: 'Error updating page types', type: 'error' })
      } finally {
        this.isLoading.updatePageType = false
      }
    },
    getRowIndex (page) {
      if (!page) return -1
      const items = this.hierarchicalPages || []
      return items.findIndex(p => p.page_id === page.page_id)
    },
    toggleRangeSelection (targetIndex, shouldSelect) {
      const items = this.hierarchicalPages || []
      if (!items.length) return
      if (targetIndex < 0 || targetIndex >= items.length) return

      const anchorIndex = this.lastSelectedIndex ?? targetIndex
      const start = Math.min(anchorIndex, targetIndex)
      const end = Math.max(anchorIndex, targetIndex)
      const rangeItems = items.slice(start, end + 1)
      const selectedIds = new Set((this.selectedRows || []).map(page => page.page_id))

      if (shouldSelect) {
        rangeItems.forEach(page => selectedIds.add(page.page_id))
      } else {
        rangeItems.forEach(page => selectedIds.delete(page.page_id))
      }

      this.selectedRows = items.filter(page => selectedIds.has(page.page_id))
      this.lastSelectedIndex = targetIndex
    },
    handleRowCheckboxToggle (event, item, select, isSelected) {
      const targetIndex = this.getRowIndex(item)
      if (targetIndex === -1) return

      if (event.shiftKey && this.lastSelectedIndex !== null) {
        event.preventDefault()
        this.toggleRangeSelection(targetIndex, true)
      } else {
        select(!isSelected)
        this.lastSelectedIndex = targetIndex
      }
    },
    handleRowClick (item, event) {
      const targetIndex = this.getRowIndex(item)
      if (targetIndex === -1) return

      if (event && event.shiftKey && this.lastSelectedIndex !== null) {
        if (typeof event.preventDefault === 'function') event.preventDefault()
        if (typeof event.stopPropagation === 'function') event.stopPropagation()
        this.toggleRangeSelection(targetIndex, true)
      } else {
        this.lastSelectedIndex = targetIndex
      }
    },
    getTypeColor (type) {
      const typeObj = pageTypes.find(t => t.value === type)
      return typeObj?.color || '#E0E0E0'
    },
    getTypeText (type) {
      const typeObj = pageTypes.find(t => t.value === type)
      return typeObj?.text || type || 'Unknown'
    },
    getIndentationStyle (page) {
      const level = page._hierarchyLevel || 0
      return {
        paddingLeft: `${level * 20}px`
      }
    },
    getStockColor (status) {
      if (!status) return 'grey'
      const s = String(status).toLowerCase()
      if (s.includes('in')) return 'green'
      if (s.includes('back')) return 'orange'
      if (s.includes('pre')) return 'orange'
      if (s.includes('out')) return 'red'
      return 'grey'
    },
    formatStockStatus (status) {
      if (!status) return '—'
      const str = String(status).replace(/_/g, ' ')
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    async refreshPages () {
      this.isLoading.refresh = true
      try {
        await this.loadData()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.refresh = false
      }
    }
  }
}
